import React, { useState, useRef } from "react";
import {
  Typography,
  Container,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  Paper,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Link as MuiLink,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import MicIcon from '@mui/icons-material/Mic';
import MouseIcon from '@mui/icons-material/Mouse';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SavingsIcon from '@mui/icons-material/Savings';
import StarIcon from '@mui/icons-material/Star';
import { Helmet } from "react-helmet";
import Header from "../common/Header";
import axios from "axios";

// API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const topSectionRef = useRef<HTMLDivElement>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Newsletter state
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  
  // Testimonials from the website
  const testimonials = [
    {
      name: "Tom B.",
      role: "Field Sales",
      stars: 5,
      text: "I am in sales and as part of my role I log every interaction with customers in our CRM. Stopping after every meeting to log those interactions takes time away from meeting with other potential customers. I love the software as it helps me save time in preparing and logging those interactions on the go!"
    },
    {
      name: "Connor B.",
      role: "Co-Founder",
      stars: 5,
      text: "Digging the professional/concise filter. The software helps me to write summary updates to my team. I can ramble and it will take the essence of what I'm saying and write it more effectively than I can!"
    },
    {
      name: "Dan H.",
      role: "Account Executive",
      stars: 5,
      text: "I use Tranzcribe to help me prepare executive summaries and reports. So much easier than having to type it all out myself. Huge time saver for me."
    }
  ];

  // Auto advance testimonials
  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleStartTranscribing = () => {
    navigate("/start");
  };

  const handleScrollToVideo = () => {
    videoSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  };

  const handleScrollToTop = () => {
    topSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  
  // Newsletter subscription handler
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribeStatus('loading');
    
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => {
        setSubscribeStatus('idle');
      }, 5000);
    } catch (error) {
      setSubscribeStatus('error');
      setTimeout(() => {
        setSubscribeStatus('idle');
      }, 5000);
    }
  };

  return (
    <Box ref={topSectionRef}>
      <Helmet>
        <title>AI-Powered Voice to Text Transcription | Tranzcribe</title>
        <meta 
          name="description" 
          content="Transform your voice into text instantly with our AI-powered transcription. Save hours of typing and focus on what matters most - all at an affordable price." 
        />
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)",
          pt: { xs: 6, md: 12 },
          pb: { xs: 6, md: 3 },
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: "800px", margin: "0 auto", position: "relative", zIndex: 2 }}>
            <Typography
              variant={isSmallScreen ? "h3" : "h2"}
              component="h1"
              gutterBottom
              sx={{ 
                fontWeight: "700", 
                color: "#333",
                mb: 4,
                lineHeight: 1.2
              }}
            >
              Eliminate Typing,{' '}
              <span style={{ 
                background: 'linear-gradient(90deg, #1976d2, #42a5f5)', 
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Boost Productivity
              </span>
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                fontWeight: "400",
                color: "#555",
                mb: 5,
                lineHeight: 1.6,
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Transform your voice into text instantly with our AI-powered transcription. 
              Save hours of typing and focus on what matters most - all at an affordable price.
            </Typography>
            
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleStartTranscribing}
                sx={{
                  padding: "12px 24px",
                  fontWeight: "600",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  borderRadius: "8px",
                }}
                endIcon={<ArrowForwardIcon />}
              >
                Start Transcribing
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleScrollToVideo}
                sx={{
                  padding: "12px 24px",
                  fontWeight: "600",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  borderRadius: "8px",
                }}
              >
                Watch Demo
              </Button>
            </Box>
            
            <Box sx={{ 
              display: "flex", 
              justifyContent: "center", 
              gap: { xs: 2, md: 4 }, 
              mt: 5,
              flexWrap: "wrap"
            }}>
              {[
                { icon: <AccessTimeIcon fontSize="small" color="primary" />, text: 'No Training Required' },
                { icon: <FlashOnIcon fontSize="small" color="primary" />, text: 'Instant Results' },
                { icon: <SavingsIcon fontSize="small" color="primary" />, text: 'Budget-Friendly' }
              ].map((feature, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1,
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                    borderRadius: "20px",
                    padding: "6px 16px"
                  }}
                >
                  {feature.icon}
                  <Typography variant="body2" fontWeight={500}>
                    {feature.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
        {/* Background decoration */}
        <Box sx={{ 
          position: "absolute", 
          inset: 0, 
          background: "radial-gradient(circle at top right, rgba(25, 118, 210, 0.08), transparent 70%)" 
        }} />
      </Box>

      {/* Demo Video Section */}
      <Box 
        ref={videoSectionRef}
        sx={{ 
          py: { xs: 8, md: 2 }, 
          bgcolor: "#f8faff" 
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              width: "100%", 
              height: { xs: "50vh", md: "60vh" },
              maxWidth: "900px",
              mx: "auto",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              position: "relative",
              paddingTop: "56.25%", // 16:9 aspect ratio
              bgcolor: "#141E17"
            }}
          >
            <iframe
              src="https://player.vimeo.com/video/1089813325?background=1&autoplay=1&loop=1&muted=1"
              frameBorder="0"
              allow="autoplay; fullscreen"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none"
              }}
              title="Tranzcribe Demo"
            />
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "700", color: "#333" }}
            >
              Work Smarter, Not Harder
            </Typography>
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ maxWidth: "700px", mx: "auto" }}
            >
              Tranzcribe helps you eliminate repetitive tasks and boost your efficiency
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                title: "Effortless Efficiency",
                description: "Simply speak and watch your words transform into text instantly. No typing skills required - just your voice.",
                icon: <MicIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              },
              {
                title: "Zero Learning Curve",
                description: "Start using Tranzcribe immediately with no training needed. Our intuitive interface makes it simple for everyone.",
                icon: <MouseIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              },
              {
                title: "Budget-Friendly",
                description: "Get professional-grade transcription at a fraction of the cost. Our affordable plans fit any budget.",
                icon: <AttachMoneyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: "100%", 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      transform: "translateY(-5px)"
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: "50%", 
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: "600" }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f5f9ff" }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "700", color: "#333" }}
            >
              What Our Users Say
            </Typography>
          </Box>

          <Box 
            sx={{ 
              position: 'relative',
              width: '100%',
              mx: 'auto',
              overflow: 'hidden'
            }}
          >
            {/* Testimonial Slider */}
            <Box 
              sx={{
                position: 'relative',
                width: '100%',
                transition: 'transform 0.5s ease',
                transform: `translateX(-${activeTestimonial * 100}%)`,
                display: 'flex',
              }}
            >
              {testimonials.map((testimonial, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    flex: '0 0 100%',
                    padding: 2
                  }}
                >
                  <Paper
                    elevation={2}
                    sx={{ 
                      p: 8, 
                      maxWidth: '3xl',
                      mx: 'auto',
                      borderRadius: "16px",
                      bgcolor: 'white'
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "600" }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {testimonial.role}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', mt: 4 }}>
                      {[...Array(5)].map((_, i) => (
                        <StarIcon 
                          key={i}
                          sx={{ 
                            color: 'yellow', 
                            fill: i < testimonial.stars ? 'yellow' : 'none',
                            width: '20px',
                            height: '20px'
                          }} 
                        />
                      ))}
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ mt: 4, color: 'text.secondary' }}
                    >
                      {testimonial.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
            
            {/* Slider dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              {testimonials.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    mx: 1,
                    borderRadius: '50%',
                    bgcolor: index === activeTestimonial ? 'primary.main' : 'grey.300',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 10, md: 16 }, 
          bgcolor: theme.palette.primary.main,
          color: "white",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Container>
          <Box sx={{ textAlign: "center", maxWidth: "800px", mx: "auto", position: "relative", zIndex: 2 }}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: "700" }}
            >
              Start Saving Time Today
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 5, opacity: 0.9 }}
            >
              Join innovative professionals who are revolutionizing their workflow with AI-powered transcription
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartTranscribing}
              sx={{
                padding: "12px 32px",
                fontWeight: "600",
                textTransform: "none",
                fontSize: "1.1rem",
                borderRadius: "8px",
                bgcolor: "white",
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                }
              }}
            >
              Try for Free
            </Button>
          </Box>
        </Container>
        {/* Background decoration */}
        <Box sx={{ 
          position: "absolute", 
          inset: 0, 
          background: "radial-gradient(circle at center, rgba(255, 255, 255, 0.1), transparent 70%)" 
        }} />
      </Box>

      {/* Footer Section */}
      <Box sx={{ 
        borderTop: '1px solid', 
        borderColor: 'divider', 
        bgcolor: '#f9fafb',
        py: { xs: 6, md: 8 } 
      }}>
        <Container>
          <Grid container spacing={6}>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <MuiLink
                  component={RouterLink}
                  to="/start"
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  Pricing
                </MuiLink>
                <MuiLink
                  component={RouterLink}
                  onClick={handleScrollToVideo}
                  to=""
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  Features
                </MuiLink>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <MuiLink
                  component={RouterLink}
                  to=""
                  onClick={handleScrollToTop}
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  About
                </MuiLink>
                <MuiLink
                  component={RouterLink}
                  to="/contact"
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  Contact
                </MuiLink>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <MuiLink
                  component={RouterLink}
                  to="/privacy-policy"
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  Privacy Policy
                </MuiLink>
                <MuiLink
                  component={RouterLink}
                  to="/terms-of-service" 
                  sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  Terms of Service
                </MuiLink>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Newsletter
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Subscribe to our newsletter for updates and news
              </Typography>
              
              <Box component="form" onSubmit={handleSubscribe}>
                <TextField 
                  fullWidth
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  size="small"
                  required
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { 
                      borderRadius: 1,
                    }
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={subscribeStatus === 'loading'}
                  sx={{
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {subscribeStatus === 'loading' ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : 'Subscribe'}
                </Button>
                
                {subscribeStatus === 'success' && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Successfully subscribed!
                  </Alert>
                )}
                
                {subscribeStatus === 'error' && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to subscribe. Please try again.
                  </Alert>
                )}
              </Box>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 5 }} />
          
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            © {new Date().getFullYear()} Tranzcribe. All rights reserved.
          </Typography>
        </Container>
      </Box>


    </Box>
  );
};

export default Homepage;

